import React, { memo, useCallback, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import Icon from '../AppIcon';
import Button from '../ui/Button';
import SimpleHiringActions from './SimpleHiringActions';
import { useAuth } from '../../hooks/useAuth';

/**
 * Optimized Interview Card Component
 * Memoized to prevent unnecessary re-renders
 */
const InterviewCard = memo(({ 
  interview,
  onReschedule,
  onCancel,
  onComplete,
  onMessage,
  onStatusChange,
  loading = false,
  isClickable = false,
  onClick
}) => {
  const [avatarError, setAvatarError] = useState(false);
  const { userProfile } = useAuth();
  
  // Determine if current user is VA or employer
  const isVA = userProfile?.role === 'va';
  const isEmployer = userProfile?.role === 'employer';

  // Reset avatar error when interview changes
  useEffect(() => {
    setAvatarError(false);
  }, [interview.candidate_avatar]);

  // Memoized handlers to prevent re-renders in parent
  const handleReschedule = useCallback(() => {
    onReschedule?.(interview.id);
  }, [interview.id, onReschedule]);

  const handleCancel = useCallback(() => {
    onCancel?.(interview.id);
  }, [interview.id, onCancel]);

  const handleComplete = useCallback(() => {
    onComplete?.(interview.id);
  }, [interview.id, onComplete]);

  const handleMessage = useCallback(() => {
    onMessage?.(interview);
  }, [interview, onMessage]);

  const handleStatusChange = useCallback((interviewId, newStatus) => {
    onStatusChange?.(interviewId, newStatus);
  }, [onStatusChange]);

  const handleCardClick = useCallback((e) => {
    // Don't trigger card click if clicking on buttons or other interactive elements
    if (e.target.closest('button') || e.target.closest('a')) {
      return;
    }
    
    if (isClickable && onClick) {
      onClick(interview);
    } else if (interview.status === 'scheduled') {
      handleReschedule();
    }
  }, [isClickable, onClick, interview, handleReschedule]);

  // Helper functions
  const getStatusColor = (status) => {
    switch (status) {
      case 'scheduled': return 'bg-blue-100 text-blue-800';
      case 'completed': return 'bg-green-100 text-green-800';
      case 'cancelled': return 'bg-red-100 text-red-800';
      case 'rescheduled': return 'bg-yellow-100 text-yellow-800';
      case 'expired': return 'bg-gray-100 text-gray-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getTypeIcon = (type) => {
    switch (type) {
      case 'video': return 'Video';
      case 'phone': return 'Phone';
      case 'in-person': return 'MapPin';
      default: return 'Calendar';
    }
  };

  const isToday = () => {
    if (!interview.scheduled_at) return false;
    
    const interviewDate = new Date(interview.scheduled_at);
    if (isNaN(interviewDate.getTime())) return false;
    
    const today = new Date();
    return interviewDate.toDateString() === today.toDateString();
  };

  const isUpcoming = () => {
    if (!interview.scheduled_at) return false;
    
    const interviewDateTime = new Date(interview.scheduled_at);
    if (isNaN(interviewDateTime.getTime())) return false;
    
    return interviewDateTime > new Date();
  };

  const isExpired = () => {
    if (!interview.scheduled_at) return false;
    
    const interviewDateTime = new Date(interview.scheduled_at);
    if (isNaN(interviewDateTime.getTime())) return false;
    
    // Interview is expired if the date has passed and it's not already completed or cancelled
    return interviewDateTime < new Date() && interview.status !== 'completed' && interview.status !== 'cancelled';
  };

  const formatDate = (date) => {
    if (!date || date === 'TBD') return 'TBD';
    
    const dateObj = new Date(date);
    if (isNaN(dateObj.getTime())) return 'TBD';
    
    return dateObj.toLocaleDateString('en-US', {
      weekday: 'short',
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  // Enhanced date formatting similar to VA's interviews page
  const formatDateTime = (dateTime) => {
    if (!dateTime) return { date: 'TBD', time: 'TBD' };
    
    const date = new Date(dateTime);
    if (isNaN(date.getTime())) return { date: 'TBD', time: 'TBD' };
    
    const today = new Date();
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);
    
    const dateStr = date.toDateString();
    const todayStr = today.toDateString();
    const tomorrowStr = tomorrow.toDateString();
    
    let formattedDate;
    if (dateStr === todayStr) {
      formattedDate = 'Today';
    } else if (dateStr === tomorrowStr) {
      formattedDate = 'Tomorrow';
    } else {
      formattedDate = date.toLocaleDateString('en-US', {
        weekday: 'short',
        month: 'short',
        day: 'numeric'
      });
    }
    
    const formattedTime = date.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
    
    return { date: formattedDate, time: formattedTime };
  };

  // Render action buttons based on user role
  const renderActionButtons = () => {
    if (isVA) {
      // VA-specific buttons
      return (
        <div className="flex items-center space-x-3 flex-wrap gap-1">
          {interview.status === 'scheduled' && !isExpired() && (
            <>
              {/* Join meeting button - primary action for VAs */}
              <Button
                variant="primary"
                size="sm"
                iconName="ExternalLink"
                onClick={(e) => {
                  e.stopPropagation();
                  if (interview.meeting_link) {
                    window.open(interview.meeting_link, '_blank');
                  }
                }}
                disabled={!interview.meeting_link}
              >
                Join Meeting
              </Button>
              
              {/* Request reschedule button */}
              <Button
                variant="outline"
                size="sm"
                iconName="Calendar"
                onClick={(e) => {
                  e.stopPropagation();
                  handleReschedule();
                }}
                disabled={loading}
              >
                Request Reschedule
              </Button>
            </>
          )}

          {interview.status === 'completed' && interview.hiring_status !== 'not_moving_forward' && (
            <>
              {/* Message employer button */}
              <Button
                variant="outline"
                size="sm"
                iconName="MessageSquare"
                onClick={(e) => {
                  e.stopPropagation();
                  handleMessage();
                }}
                disabled={loading}
              >
                Message Employer
              </Button>
            </>
          )}
          
          {interview.status === 'completed' && (
            <>
              {/* View details button */}
              <Button
                variant="outline"
                size="sm"
                iconName="Eye"
                onClick={(e) => {
                  e.stopPropagation();
                  if (onClick) onClick(interview);
                }}
                disabled={loading}
              >
                View Feedback
              </Button>
            </>
          )}

          {interview.status === 'cancelled' && (
            <div className="text-sm text-red-600 italic">
              Interview cancelled - Contact employer for rescheduling
            </div>
          )}

          {isExpired() && (
            <div className="text-sm text-gray-600 italic">
              Interview date has passed - Contact employer to reschedule
            </div>
          )}
        </div>
      );
    }

    if (isEmployer) {
      // Employer-specific buttons (existing functionality)
      return (
        <>
          <div className="flex items-center space-x-3 flex-wrap gap-1">
            {interview.status === 'scheduled' && (
              <>
                <Button
                  variant="primary"
                  size="sm"
                  iconName="ExternalLink"
                  onClick={(e) => {
                    e.stopPropagation();
                    if (interview.meeting_link) {
                      window.open(interview.meeting_link, '_blank');
                    }
                  }}
                  disabled={!interview.meeting_link}
                >
                  Join
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  iconName="CheckCircle"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleComplete();
                  }}
                  disabled={loading}
                >
                  Done
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  iconName="X"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleCancel();
                  }}
                  disabled={loading}
                >
                  Cancel
                </Button>
              </>
            )}

            {interview.status === 'completed' && (
              <Button
                variant="outline"
                size="sm"
                iconName="MessageSquare"
                onClick={(e) => {
                  e.stopPropagation();
                  handleMessage();
                }}
                disabled={loading}
              >
                Message Candidate
              </Button>
            )}

            {interview.status === 'cancelled' && (
              <Button
                variant="outline"
                size="sm"
                iconName="RotateCcw"
                onClick={(e) => {
                  e.stopPropagation();
                  handleReschedule();
                }}
                disabled={loading}
              >
                Reschedule
              </Button>
            )}

            {isExpired() && (
              <Button
                variant="outline"
                size="sm"
                iconName="RotateCcw"
                onClick={(e) => {
                  e.stopPropagation();
                  handleReschedule();
                }}
                disabled={loading}
              >
                Reschedule
              </Button>
            )}
          </div>

          {/* Hiring decision actions - only for employers with completed interviews */}
          {interview.status === 'completed' && (
            <SimpleHiringActions
              interview={interview}
              onStatusChange={handleStatusChange}
              loading={loading}
            />
          )}
        </>
      );
    }

    // Default fallback for unknown roles
    return null;
  };

  return (
    <div 
      className={`bg-surface rounded-lg border transition-all duration-200 p-4 ${
        isClickable || (interview.status === 'scheduled' && !isExpired()) ? 'cursor-pointer' : ''
      } ${
        isToday() && interview.status === 'scheduled' && !isExpired()
          ? 'border-primary shadow-md bg-primary-50' 
          : isExpired()
          ? 'border-gray-300 opacity-75'
          : 'border-border hover:border-primary hover:shadow-md'
      }`}
      onClick={handleCardClick}
      role="button"
      tabIndex={isClickable || (interview.status === 'scheduled' && !isExpired()) ? 0 : -1}
      onKeyDown={(e) => {
        if ((isClickable || (interview.status === 'scheduled' && !isExpired())) && (e.key === 'Enter' || e.key === ' ')) {
          e.preventDefault();
          handleCardClick(e);
        }
      }}
    >
      {/* Header with candidate info and status */}
      <div className="flex items-start justify-between mb-4 flex-wrap gap-2 truncate">
        <div className="flex items-start space-x-4">
          <div className="w-12 h-12 bg-primary-100 rounded-full flex items-center justify-center flex-shrink-0 relative">
            {interview.candidate_avatar && !avatarError ? (
              <img
                src={interview.candidate_avatar}
                alt={interview.candidate_name}
                className="w-12 h-12 rounded-full object-cover"
                onError={() => setAvatarError(true)}
              />
            ) : (
              <Icon name="User" size={20} className="text-primary" />
            )}
          </div>
          <div className="min-w-0 flex-1">
            <h3 className="font-semibold text-text-primary">
              {isVA ? interview.employer_name || 'Employer' : interview.candidate_name}
            </h3>
            <p className="text-text-secondary text-sm ">
              {interview.job_title}
            </p>
            <p className="text-text-muted text-xs">
              {isVA ? interview.employer_email || 'Contact through platform' : interview.candidate_email}
            </p>
            
            {/* Status badges */}
            <div className="flex items-center space-x-2 mt-2">
              {isToday() && interview.status === 'scheduled' && (
                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-amber-100 text-amber-800">
                  <Icon name="Clock" size={12} className="mr-1" />
                  Today
                </span>
              )}
              {interview.considered_for_hire && (
                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                  <Icon name="UserCheck" size={12} className="mr-1" />
                  Considered for Hire
                </span>
              )}
              {isUpcoming() && interview.status === 'scheduled' && (
                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                  <Icon name="Calendar" size={12} className="mr-1" />
                  Upcoming
                </span>
              )}
              {isExpired() && (
                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                  <Icon name="AlertCircle" size={12} className="mr-1" />
                  Expired
                </span>
              )}
            </div>
          </div>
        </div>
        
        <span className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(isExpired() ? 'expired' : interview.status)} flex-shrink-0`}>
          {isExpired() ? 'Expired' : interview.status.charAt(0).toUpperCase() + interview.status.slice(1)}
        </span>
      </div>

      {/* Interview details */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4 text-sm">
        <div className="flex items-center space-x-2">
          <Icon name="Calendar" size={16} className="text-text-muted flex-shrink-0" />
          <span className="text-text-secondary truncate">
            {formatDateTime(interview.scheduled_at).date}
          </span>
        </div>
        <div className="flex items-center space-x-2">
          <Icon name="Clock" size={16} className="text-text-muted flex-shrink-0" />
          <span className="text-text-secondary">
            {formatDateTime(interview.scheduled_at).time}
          </span>
        </div>
        <div className="flex items-center space-x-2">
          <Icon name={getTypeIcon(interview.type)} size={16} className="text-text-muted flex-shrink-0" />
          <span className="text-text-secondary">
            {interview.duration} min
          </span>
        </div>
        {interview.rating && (
          <div className="flex items-center space-x-2">
            <Icon name="Star" size={16} className="text-yellow-500 flex-shrink-0" />
            <span className="text-text-secondary">
              {interview.rating}/5
            </span>
          </div>
        )}
      </div>

      {/* Notes - only show for employers or if it's a general note */}
      {interview.notes && (isEmployer || interview.notes_public) && (
        <div className="mb-4">
          <p className="text-text-secondary text-sm">
            <span className="font-medium">Notes:</span> {interview.notes}
          </p>
        </div>
      )}

      {/* Feedback - show for VAs if completed, for employers always */}
      {interview.feedback && (interview.status === 'completed' || isEmployer) && (
        <div className="mb-4 p-3 bg-secondary-50 rounded-lg">
          <p className="text-text-secondary text-sm">
            <span className="font-medium">
              {isVA ? 'Interview Feedback:' : 'Feedback:'}
            </span> {interview.feedback}
          </p>
        </div>
      )}

      {/* Hiring notes - only show for employers */}
      {interview.hiring_notes && isEmployer && (
        <div className="mb-4 p-3 bg-blue-50 rounded-lg">
          <p className="text-text-secondary text-sm">
            <span className="font-medium">Hiring Notes:</span> {interview.hiring_notes}
          </p>
        </div>
      )}

      {/* Action buttons */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        {renderActionButtons()}
      </div>

      {/* Hiring status indicator for completed interviews */}
      {interview.status === 'completed' && interview.hiring_status && (
        <div className="mt-3 pt-3 border-t border-border">
          <div className="flex items-center justify-between">
            <span className="text-sm text-text-secondary">
              {isVA ? 'Application Status:' : 'Hiring Status:'}
            </span>
            <span className={`px-2 py-1 rounded-full text-xs font-medium ${
              interview.hiring_status === 'hired' 
                ? 'bg-green-100 text-green-800'
                : interview.hiring_status === 'not_moving_forward'
                ? 'bg-red-100 text-red-800'
                : interview.hiring_status === 'under_consideration'
                ? 'bg-yellow-100 text-yellow-800'
                : 'bg-blue-100 text-blue-800'
            }`}>
              {interview.hiring_status.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
            </span>
          </div>
          
          {interview.offer_amount && (isVA || isEmployer) && (
            <div className="flex items-center justify-between mt-1">
              <span className="text-sm text-text-secondary">
                {isVA ? 'Offer:' : 'Offer Amount:'}
              </span>
              <span className="text-sm font-medium text-green-600">
                ${interview.offer_amount}/hr
              </span>
            </div>
          )}
          
          {interview.decision_date && (
            <div className="flex items-center justify-between mt-1">
              <span className="text-sm text-text-secondary">Decision Date:</span>
              <span className="text-sm text-text-secondary">
                {formatDate(interview.decision_date)}
              </span>
            </div>
          )}
        </div>
      )}

      {/* VA-specific status messages */}
      {isVA && interview.status === 'scheduled' && !isExpired() && !interview.meeting_link && (
        <div className="mt-3 pt-3 border-t border-border">
          <div className="flex items-start space-x-2">
            <Icon name="Info" size={16} className="text-blue-500 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-blue-700">
              Meeting link will be provided closer to the interview time.
            </p>
          </div>
        </div>
      )}

      {isVA && interview.status === 'completed' && !interview.feedback && (
        <div className="mt-3 pt-3 border-t border-border">
          <div className="flex items-start space-x-2">
            <Icon name="Clock" size={16} className="text-amber-500 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-amber-700">
              Feedback from the employer is pending. You'll be notified once it's available.
            </p>
          </div>
        </div>
      )}

      {isExpired() && (
        <div className="mt-3 pt-3 border-t border-border">
          <div className="flex items-start space-x-2">
            <Icon name="AlertCircle" size={16} className="text-gray-500 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-gray-600">
              This interview date has passed. {isVA ? 'Request a reschedule' : 'Reschedule'} if you'd like to proceed.
            </p>
          </div>
        </div>
      )}
    </div>
  );
});

InterviewCard.propTypes = {
  interview: PropTypes.shape({
    id: PropTypes.string.isRequired,
    candidate_name: PropTypes.string.isRequired,
    candidate_email: PropTypes.string,
    candidate_avatar: PropTypes.string,
    employer_name: PropTypes.string,
    employer_email: PropTypes.string,
    job_title: PropTypes.string.isRequired,
    scheduled_date: PropTypes.string, // Can be date string or 'TBD'
    scheduled_time: PropTypes.string, // Can be time string or 'TBD'
    scheduled_at: PropTypes.oneOfType([
      PropTypes.string, 
      PropTypes.instanceOf(Date),
      PropTypes.oneOf([null])
    ]),
    duration: PropTypes.number,
    type: PropTypes.oneOf(['video', 'phone', 'in-person']),
    status: PropTypes.oneOf(['scheduled', 'completed', 'cancelled', 'rescheduled', 'expired', 'not_moving_forward']).isRequired,
    meeting_link: PropTypes.string,
    phone_number: PropTypes.string,
    notes: PropTypes.string,
    notes_public: PropTypes.bool,
    feedback: PropTypes.string,
    rating: PropTypes.number,
    hiring_status: PropTypes.oneOf(['interviewed', 'under_consideration', 'hired', 'not_moving_forward']),
    hiring_notes: PropTypes.string,
    considered_for_hire: PropTypes.bool,
    offer_amount: PropTypes.number,
    decision_date: PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(Date)]),
    rejection_reason: PropTypes.string
  }).isRequired,
  onReschedule: PropTypes.func,
  onCancel: PropTypes.func,
  onComplete: PropTypes.func,
  onMessage: PropTypes.func,
  onStatusChange: PropTypes.func,
  loading: PropTypes.bool,
  isClickable: PropTypes.bool,
  onClick: PropTypes.func
};

InterviewCard.displayName = 'InterviewCard';

export default InterviewCard;